import { useVirtualizer } from "@tanstack/react-virtual";
import type { RefObject } from "react";
import { NoticeConversationRow } from "./notice-conversation-row";
import type { NoticeConversation } from "./notice-conversations";

// Roughly a 64px min-height row plus the 2px inter-row gap; measureElement
// corrects each row's real height, so this only seeds the initial window.
const NOTICE_ROW_ESTIMATE_PX = 66;
const NOTICE_ROW_OVERSCAN = 8;

interface NoticeFlatListProps {
  scrollRef: RefObject<HTMLDivElement | null>;
  conversations: NoticeConversation[];
  collapsed: boolean;
  selectedChannel: string | null;
  showBadge: boolean;
  onRowOpen: (conversation: NoticeConversation, position: number) => void;
  onBadgeImpression: (
    conversation: NoticeConversation,
    position: number,
  ) => void;
}

export function NoticeFlatList({
  scrollRef,
  conversations,
  collapsed,
  selectedChannel,
  showBadge,
  onRowOpen,
  onBadgeImpression,
}: NoticeFlatListProps) {
  const virtualizer = useVirtualizer({
    count: conversations.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => NOTICE_ROW_ESTIMATE_PX,
    overscan: NOTICE_ROW_OVERSCAN,
    // Key by conversation id so measurements and scroll offset stay anchored
    // to the same rows across React Query refetches that reorder the list.
    getItemKey: (index) => conversations[index].id,
  });

  return (
    <div
      className="relative w-full shrink-0"
      style={{ height: `${virtualizer.getTotalSize()}px` }}
    >
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const conversation = conversations[virtualRow.index];
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            className="absolute left-0 top-0 w-full pb-0.5"
            style={{ transform: `translateY(${virtualRow.start}px)` }}
          >
            <NoticeConversationRow
              conversation={conversation}
              collapsed={collapsed}
              selected={selectedChannel === conversation.id}
              showBadge={showBadge}
              onSelect={() => onRowOpen(conversation, virtualRow.index)}
              onBadgeImpression={() =>
                onBadgeImpression(conversation, virtualRow.index)
              }
            />
          </div>
        );
      })}
    </div>
  );
}
